import { FC, Fragment, useMemo, useState } from 'react'
import { cn } from 'src/utils/cn'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import styles from './platformBreakdown.module.scss'
import {
  CommentPlatformRow,
  ContentCampaign,
  PlatformSentiment,
} from '../../interfaces'

interface PlatformBreakdownProps {
  campaigns: ContentCampaign[]
  // Every platform any campaign published on, in the order the tabs offer
  // them.
  platforms: string[]
  rows: CommentPlatformRow[]
}

const COPY = {
  allPlatforms: 'All Platforms',
}

const BANDS = [
  { color: '#8080ff', key: 'positive', label: 'Positive' },
  { color: '#c7ced1', key: 'neutral', label: 'Neutral' },
  { color: '#fa7138', key: 'negative', label: 'Negative' },
] as const

const ALL_TAB = 'all'

// The axis counts up in steps this size, always ending at least one step past
// the longest bar — so the ticks read 0, 500, 1000 rather than stopping on a
// bar's own total, and the longest bar never runs to the plot's edge.
// Roughly this many steps across the axis; the step itself is rounded to a
// value a reader can do arithmetic with. A fixed step counted to 500 whatever
// the data was, so a busiest row of 131 drew a bar a quarter of the track wide
// under an axis reading 0 and 500.
// A segment narrower than this share of its bar cannot hold its own number
// without it spilling over the join into the next one.
const SEGMENT_LABEL_FLOOR = 9

// And a share of its bar is not enough on its own: a lone segment is 100% of a
// bar that may still be a few pixels wide against the axis.
const SEGMENT_AXIS_FLOOR = 3

const AXIS_STEPS = 5

const axisStepFor = (largest: number) => {
  const rough = Math.max(1, largest) / AXIS_STEPS
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const nice =
    [1, 2, 2.5, 5, 10].find((step) => step * magnitude >= rough) ?? 10

  return nice * magnitude
}

// Plain digits, no grouping: the design prints 2000 and 1500 on the bars and
// on the axis, not 2,000 and 1,500.
const formatCount = (value: number) => String(value)

const total = (row: PlatformSentiment) =>
  row.positive + row.neutral + row.negative

/*
 * Comment Volume → By platform.
 *
 * A row per campaign-and-platform pair, each split the same three ways as the
 * sentiment view, all measured against one axis across the top. Grouped under
 * the campaign rather than interleaved: the comparison the design asks for is
 * between a campaign's own platforms first, and between campaigns second.
 *
 * Built from divs rather than a chart: the rows are a table with a bar in the
 * last column, and every attempt at this in ECharts fought the campaign
 * headings sitting between groups of categories.
 */
export const PlatformBreakdown: FC<PlatformBreakdownProps> = ({
  campaigns,
  platforms,
  rows,
}) => {
  const [tab, setTab] = useState<string>(ALL_TAB)

  const groups = useMemo(
    () =>
      campaigns
        .map((campaign) => {
          const row = rows.find((item) => item.campaignId === campaign.id)
          const kept = (row?.platforms ?? []).filter(
            (platform) => tab === ALL_TAB || platform.platform === tab
          )

          return { campaign, platforms: kept }
        })
        // A campaign that published nothing on the selected platform is left
        // out rather than printed as a heading with no rows under it.
        .filter((group) => group.platforms.length > 0),
    [campaigns, rows, tab]
  )

  // One scale for every row on screen, so a bar's length means the same under
  // any campaign.
  const axisMax = useMemo(() => {
    const largest = Math.max(
      0,
      ...groups.flatMap((group) => group.platforms.map(total))
    )

    const step = axisStepFor(largest)

    return step * Math.ceil(Math.max(1, largest) / step)
  }, [groups])

  const ticks = useMemo(
    () =>
      Array.from(
        { length: Math.round(axisMax / axisStepFor(axisMax)) + 1 },
        (unused, index) => index * axisStepFor(axisMax)
      ),
    [axisMax]
  )

  // The grid's rows are declared rather than left implicit: the gridlines span
  // `2 / -1`, and -1 counts back from the last line of the EXPLICIT grid, so
  // against implicit rows it resolves to line 1 and collapses them into the
  // scale's row.
  const rowCount =
    1 + groups.reduce((count, group) => count + 1 + group.platforms.length, 0)

  return (
    <div className={styles.wrap}>
      <div className={styles.tabs}>
        <button
          className={cn(styles.tab, tab === ALL_TAB && styles.tabActive)}
          onClick={() => setTab(ALL_TAB)}
          type="button"
        >
          {COPY.allPlatforms}
        </button>

        {platforms.map((platform) => (
          <button
            className={cn(styles.tab, tab === platform && styles.tabActive)}
            key={platform}
            onClick={() => setTab(platform)}
            type="button"
          >
            {platformDisplayLabel(platform)}
          </button>
        ))}
      </div>

      {/* One grid for the scale, the labels and the bars, so the label column
          sizes itself to the longest name and the bars start right after it
          however short the campaigns are called. */}
      <div
        className={styles.grid}
        style={{ gridTemplateRows: `repeat(${rowCount}, auto)` }}
      >
        <span />

        <div className={styles.scale}>
          {ticks.map((tick, index) => (
            <span
              className={styles.tick}
              key={tick}
              style={{ left: `${(index / (ticks.length - 1)) * 100}%` }}
            >
              {formatCount(tick)}
            </span>
          ))}
        </div>

        {/* Behind every row of the plot column, never behind the labels. */}
        <div className={styles.gridlines}>
          {ticks.map((tick) => (
            <span className={styles.gridline} key={tick} />
          ))}
        </div>

        {groups.map((group) => (
          <Fragment key={group.campaign.id}>
            <p className={styles.groupName} title={group.campaign.name}>
              {group.campaign.name}
            </p>
            <span />

            {group.platforms.map((platform) => (
              <Fragment key={platform.platform}>
                <span className={styles.rowLabel}>
                  {/* The name clips inside its own box: the label cannot clip,
                      or it would cut off the rule drawn beside it. */}
                  <span className={styles.rowLabelText}>
                    {platformDisplayLabel(platform.platform)}
                  </span>
                </span>

                <span className={styles.track}>
                  {/* The bar is its own box, as wide as the row's total. The
                      rounded ends belong to it: put on the full-width track
                      they would round nothing the reader can see, since the
                      bar stops short of the track's right edge. */}
                  <span
                    className={styles.bar}
                    style={{
                      width: `${(total(platform) / axisMax) * 100}%`,
                    }}
                  >
                    {BANDS.map((band) => {
                      const value = platform[band.key]
                      // A segment of nothing is not drawn: at zero width its
                      // label still printed, landing on top of the next
                      // segment's.
                      if (!value) return null

                      const share = (value / total(platform)) * 100

                      return (
                        <span
                          className={styles.segment}
                          key={band.key}
                          style={{
                            background: band.color,
                            width: `${share}%`,
                          }}
                        >
                          {share >= SEGMENT_LABEL_FLOOR &&
                            (value / axisMax) * 100 >= SEGMENT_AXIS_FLOOR && (
                              <span className={styles.segmentValue}>
                                {formatCount(value)}
                              </span>
                            )}
                        </span>
                      )
                    })}
                  </span>
                </span>
              </Fragment>
            ))}
          </Fragment>
        ))}
      </div>

      <ul className={styles.legend}>
        {BANDS.map((band) => (
          <li className={styles.legendItem} key={band.key}>
            <span
              className={styles.legendDot}
              style={{ background: band.color }}
            />
            {band.label}
          </li>
        ))}
      </ul>
    </div>
  )
}
